<template>
  <div 
    class="sidebar-logo-container" 
    :class="{ 
      'collapse': collapse,
      'vertical-layout': shouldUseVerticalLayout
    }" 
    :style="{ fontSize: fontSize }"
  >
    <img :src="logoSrc" class="logo-icon" alt="logo" />
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import useSettingsStore from '@/store/modules/settings'
import cinemaLogo from '@/assets/logo/cinema_logo.png'
import cinemaLogoLight from '@/assets/logo/cinema_logo_light.png'

const settingsStore = useSettingsStore()
// 深色模式使用浅色版 logo（cinema_logo_light.png），浅色模式使用默认 logo
const logoSrc = computed(() => settingsStore.themeMode === 'dark' ? cinemaLogoLight : cinemaLogo)

const props = defineProps({
  collapse: {
    type: Boolean,
    required: true
  }
})

const title = import.meta.env.VITE_APP_TITLE;
const fontSize = ref('1.2rem');
const iconSize = ref('1.4rem');
const shouldUseVerticalLayout = ref(false); // 是否使用纵向布局（icon在上，title在下）

// 根据侧边栏宽度调整字体大小和布局
const updateFontSize = () => {
  if (props.collapse) {
    // 收起状态时不显示文字，保持默认大小
    fontSize.value = '1.2rem';
    iconSize.value = '1.4rem';
    shouldUseVerticalLayout.value = false;
    return;
  }
  
  const sidebar = document.querySelector('.sidebar-container');
  if (!sidebar) return;
  
  const sidebarWidth = sidebar.getBoundingClientRect().width;
  
  // 最小宽度 220px，最大宽度 420px
  // 字体大小在 0.75rem (220px) 到 1.2rem (420px) 之间线性变化
  const minWidth = 220;
  const maxWidth = 420;
  const minFontSize = 0.75;
  const maxFontSize = 1.2;
  const minIconSize = 1.0;
  const maxIconSize = 1.4;
  
  // 当宽度较窄时改为纵向布局（icon在上、title在下），避免文字换行
  const verticalLayoutThreshold = 260;
  shouldUseVerticalLayout.value = sidebarWidth <= verticalLayoutThreshold;
  
  if (sidebarWidth <= minWidth) {
    fontSize.value = `${minFontSize}rem`;
    iconSize.value = `${minIconSize}rem`;
  } else if (sidebarWidth >= maxWidth) {
    fontSize.value = `${maxFontSize}rem`;
    iconSize.value = `${maxIconSize}rem`;
  } else {
    // 线性插值
    const ratio = (sidebarWidth - minWidth) / (maxWidth - minWidth);
    const currentFontSize = minFontSize + (maxFontSize - minFontSize) * ratio;
    const currentIconSize = minIconSize + (maxIconSize - minIconSize) * ratio;
    fontSize.value = `${currentFontSize}rem`;
    iconSize.value = `${currentIconSize}rem`;
  }
};

let resizeObserver = null;

onMounted(() => {
  updateFontSize();
  
  // 使用 ResizeObserver 监听侧边栏宽度变化
  const sidebar = document.querySelector('.sidebar-container');
  if (sidebar && window.ResizeObserver) {
    resizeObserver = new ResizeObserver(() => {
      updateFontSize();
    });
    resizeObserver.observe(sidebar);
  } else {
    // 降级方案：使用 window resize 事件
    window.addEventListener('resize', updateFontSize);
  }
});

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  } else {
    window.removeEventListener('resize', updateFontSize);
  }
});

// 监听 collapse 状态变化
watch(() => props.collapse, () => {
  updateFontSize();
});
</script>

<style lang="scss" scoped>
.sidebarLogoFade-enter-active {
  transition: opacity 1.5s;
}

.sidebarLogoFade-enter,
.sidebarLogoFade-leave-to {
  opacity: 0;
}

.sidebar-logo-container {
  // position: relative;
  // width: 100%;
  // height: 50px;
  // line-height: 50px;
  // text-align: center;
  // overflow: hidden;
  box-sizing: border-box;
  height: 72px;
  min-height: 72px;
  max-height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  font-weight: 700;
  font-size: 1rem;
  color: var(--text-primary);
  background-color: var(--bg-sidebar);
  border-bottom: 1px solid var(--border-color);
  transition: padding 0.3s ease;
  flex-shrink: 0; // 防止被压缩
  
  // 移动端：进一步减少高度和 padding
  @media (max-width: 768px) {
    height: 60px;
    min-height: 60px;
    max-height: 60px;
    padding: 0 14px;
    font-size: 0.95rem;
  }
  
  // 收起状态：减少 padding
  &.collapse {
    padding: 0 12px;
  }
  
  .logo-icon {
    height: auto;
    max-height: 60px;
    width: 100%;
    max-width: 200px;
    object-fit: contain; // 保持图片比例，显示清晰
    flex-shrink: 0; // 防止图标被压缩
    transition: max-width 0.3s ease;
    align-self: center; // 图标垂直居中

    @media (max-width: 768px) {
      max-height: 50px;
      max-width: 170px;
    }
  }
  
  .logo-title {
    flex: 1;
    min-width: 0;
    white-space: nowrap; // 缩小宽度时文字不换行
    line-height: 1.5;
    transition: font-size 0.3s ease;
  }
  
  // 纵向布局：icon在上，title在下
  &.vertical-layout {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 4px 12px;
    gap: 3px; // 使用gap确保icon和title之间始终有间距
    
    @media (max-width: 768px) {
      padding: 3px 10px;
      gap: 2px;
    }
    
    .logo-icon {
      margin-right: 0 !important;
      margin-bottom: 0 !important; // 使用gap代替margin
      flex-shrink: 0;
    }
    
    .logo-title {
      flex: none;
      white-space: nowrap;
      text-align: center;
      line-height: 1.2;
      width: 100%;
      min-width: 0;
      margin-top: 0 !important;
    }
  }
  
  & .sidebar-logo-link {
    height: 100%;
    width: 100%;

    & .sidebar-logo {
      width: 32px;
      height: 32px;
      vertical-align: middle;
      margin-right: 12px;
    }

    & .sidebar-title {
      display: inline-block;
      margin: 0;
      color: #fff;
      font-weight: 600;
      line-height: 50px;
      font-size: 14px;
      // font-family: Avenir, Helvetica Neue, Arial, Helvetica, sans-serif;
      font-family: Noto Sans Japanese, sans-serif;
      vertical-align: middle;
    }
  }

  &.collapse {
    justify-content: center; // 收起时居中显示图标
    padding: 0 12px;
    gap: 0; // 收起时不需要间距
    
    .logo-icon {
      margin-right: 0 !important; // 收起时移除右边距
      margin-bottom: 0 !important;
    }
    
    .logo-title {
      display: none; // 收起时完全隐藏文字
    }
    
    span {
      display: none; // 收起时完全隐藏文字（兼容旧代码）
    }
    
    .sidebar-logo {
      margin-right: 0px;
    }
  }
  
  // 字体大小通过 JavaScript 动态调整，这里只保留基础样式
}
</style>